import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faPlus, faEdit, faTrash, faSpinner, faTimes, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { adminCompaniesAPI, adminUsersAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';
import './admin.css';

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    admin_user_id: '',
    is_active: true
  });

  useEffect(() => {
    fetchCompanies();
    fetchUsers();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await adminCompaniesAPI.getAll();
      setCompanies(response.data.companies);
    } catch (error) {
      toast.error('Failed to fetch companies');
      console.error('Fetch companies error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminUsersAPI.getAll({ per_page: 1000 });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Fetch users error:', error);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData({
      name: '',
      admin_user_id: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (company) => {
    setModalMode('edit');
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      admin_user_id: company.admin_user_id,
      is_active: company.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (modalMode === 'create') {
        await adminCompaniesAPI.create(formData);
        toast.success('Company created successfully');
      } else {
        await adminCompaniesAPI.update(selectedCompany.id, formData);
        toast.success('Company updated successfully');
      }

      setShowModal(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Are you sure you want to delete company ${company.name}?`)) {
      return;
    }

    try {
      await adminCompaniesAPI.delete(company.id);
      toast.success('Company deleted successfully');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>
          <FontAwesomeIcon icon={faBuilding} />
          Company Management
        </h1>
        <p>Manage all companies in the system</p>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">All Companies ({companies.length})</h3>
          <button className="admin-btn-sm admin-btn-primary" onClick={handleCreate}>
            <FontAwesomeIcon icon={faPlus} />
            Create Company
          </button>
        </div>

        {loading ? (
          <div className="admin-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading companies...</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faBuilding} />
            <h3>No Companies Found</h3>
            <p>Create your first company to get started</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company Name</th>
                  <th>Admin User</th>
                  <th>Employees</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td data-label="ID">{company.id}</td>
                    <td data-label="Company Name">{company.name}</td>
                    <td data-label="Admin User">{company.admin_user?.email || 'N/A'}</td>
                    <td data-label="Employees">{company.employee_count}</td>
                    <td data-label="Status">
                      <span className={`admin-badge ${company.is_active ? 'success' : 'inactive'}`}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Created">{new Date(company.created_at).toLocaleDateString()}</td>
                    <td data-label="Actions">
                      <div className="admin-actions">
                        <button
                          className="admin-btn-sm admin-btn-primary"
                          onClick={() => handleEdit(company)}
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="admin-btn-sm admin-btn-danger"
                          onClick={() => handleDelete(company)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {modalMode === 'create' ? 'Create New Company' : 'Edit Company'}
              </h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Enter company name"
                />
              </div>

              <div className="admin-form-group">
                <label>Admin User</label>
                <select
                  value={formData.admin_user_id}
                  onChange={(e) => setFormData({...formData, admin_user_id: e.target.value})}
                  required
                >
                  <option value="">Select admin user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{ width: 'auto', marginRight: '0.5rem' }}
                  />
                  Active
                </label>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-btn-sm admin-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn-sm admin-btn-primary" disabled={loading}>
                  {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : (modalMode === 'create' ? 'Create' : 'Update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
